import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import tailwindClassnames from "./TailwindClassnames";
import { InfoIcon } from "./ProjectIcons";
import { RoleScope, getRoleTable, inheritanceNote } from "./rolePermissions";

/** Elements the panel's Tab-trap and initial-focus logic consider focusable. */
const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** Breathing room kept between the panel and every viewport edge, in px. */
const GUTTER = 8;

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max));

type RolePermissionsInfoProps = {
  /** Which role set to describe — organizations have four roles, projects five. */
  scope: RoleScope;
};

/**
 * An info button that opens a small dialog explaining what each role can do,
 * and how organization roles reach projects (AKL-793).
 *
 * Opens on click and on Enter/Space and closes on Escape, the close button, a
 * click outside, or a scroll. Focus moves into the panel on open, and back to
 * the trigger on every close **except** a click outside, where the reader has
 * just chosen somewhere else to be and pulling focus back would take it off
 * the control they clicked. The ticket requires all of this to work without a
 * mouse, which the app's MUI tooltips cannot do (they open on hover, focus and
 * long-press only, and carry no dialog semantics).
 *
 * The panel is positioned `fixed` rather than `absolute`, following
 * PredictionPopover. Every surface that mounts this sits inside an ancestor
 * that clips: both user modals render in a `fixed inset-0 … overflow-y-auto`
 * overlay on a `max-w-md` panel, and the user page wraps its role tables in
 * `overflow-x-auto`. An absolutely-positioned panel is cut off in all of them,
 * and this codebase has no portal to escape with.
 *
 * Hovering or focusing the button shows a short hint saying it can be clicked.
 * That hint is suppressed while the panel is open — see the Tooltip below.
 *
 * @param scope which role set to describe
 */
const RolePermissionsInfo: React.FC<RolePermissionsInfoProps> = ({ scope }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  /** Whether hover/focus wants the hover hint shown. See the Tooltip below. */
  const [hintOpen, setHintOpen] = useState(false);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const containerRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const hasFocused = useRef(false);

  const table = getRoleTable(scope);
  const panelTitle = t(table.titleKey);

  /** Closes the panel and returns focus to the button that opened it. */
  const close = useCallback(() => {
    setIsOpen(false);
    setPosition(null);
    triggerRef.current?.focus();
  }, []);

  // Places the panel below its button, or above when there is no room below,
  // clamped inside the viewport. Measured rather than estimated, so the six
  // column project table and the four column organization one both land.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    const anchor = trigger.getBoundingClientRect();
    // Measured while the panel is still parked at left:0/top:0, so this rect's
    // origin IS its containing block's origin in viewport coordinates — which
    // is not always the viewport. `modalOverlay` carries `backdrop-blur-sm`,
    // and a computed backdrop-filter makes an element the containing block for
    // its position:fixed descendants. Inside both user modals the panel
    // therefore resolves against the overlay's padding box, and offsetting by
    // this origin is what keeps it under its trigger once that overlay has
    // been scrolled. Where there is no such ancestor — the user page — the
    // origin is (0, 0) and the arithmetic below is unchanged.
    const parked = panel.getBoundingClientRect();
    const { width, height } = parked;
    const left = clamp(anchor.left, GUTTER, window.innerWidth - width - GUTTER);
    const below = anchor.bottom + GUTTER;
    const fitsBelow = below + height <= window.innerHeight - GUTTER;
    const top = fitsBelow
      ? below
      : clamp(
          anchor.top - GUTTER - height,
          GUTTER,
          window.innerHeight - height - GUTTER,
        );
    setPosition({ left: left - parked.left, top: top - parked.top });
  }, [isOpen]);

  /**
   * Focuses the first focusable element once the panel is open *and* placed.
   *
   * It must wait for `position`: on the first commit after opening, the panel
   * has not been measured yet and is still transparent, and a browser will
   * not move focus into something it does not consider rendered. The `hasFocused`
   * ref keeps it to once per open — the way ReviewPageFilter's short dependency
   * array does — so a later re-render cannot yank focus back out of wherever
   * the reader has tabbed to.
   *
   * jsdom enforces none of this, so a test cannot catch a regression here.
   * Verified by hand in Chrome.
   */
  useEffect(() => {
    if (!isOpen) {
      hasFocused.current = false;
      return;
    }
    if (!position || hasFocused.current) return;
    const panel = panelRef.current;
    if (!panel) return;
    hasFocused.current = true;
    const focusable = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    Array.from(focusable)
      .find((el) => !el.hasAttribute("disabled"))
      ?.focus();
  }, [isOpen, position]);

  // While open: Escape closes, and Tab wraps inside the panel.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute("disabled"));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      // Clicking the panel's text moves focus off the Close button — onto the
      // panel itself, or the body. Either way `active` is not one of the
      // focusable children, and an identity-only check would let the next Tab
      // escape into the modal behind an open dialog.
      if (!active || focusable.indexOf(active) === -1) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, close]);

  // A click anywhere outside the button and its panel closes it. Bound only
  // while open so the rest of the surface pays no cost.
  //
  // Deliberately NOT `close()`: this is the one path that must not restore
  // focus to the trigger. The reader has just clicked something else — often
  // Cancel, or the role dropdown itself — and yanking focus back to the info
  // icon would take it off the control they were reaching for. Escape, the
  // close button and a scroll all restore focus, because none of them names a
  // new place for it to go.
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (
        containerRef.current &&
        target &&
        !containerRef.current.contains(target)
      ) {
        setIsOpen(false);
        setPosition(null);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [isOpen]);

  // The panel is placed once, so scrolling the page behind it or resizing the
  // window would leave it floating away from its trigger. Close instead of
  // chasing: it is a short-lived reference panel, and closing returns focus to
  // the trigger rather than stranding it in something scrolled off screen.
  // Scrolling *inside* the panel is ignored — it has its own overflow-y-auto.
  useEffect(() => {
    if (!isOpen) return;
    const onScroll = (e: Event) => {
      const target = e.target as Node | null;
      if (panelRef.current && target && panelRef.current.contains(target))
        return;
      close();
    };
    const onResize = () => close();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [isOpen, close]);

  const headCell = `${tailwindClassnames.tableCardHeadCell} whitespace-nowrap px-3 py-2`;
  // px-2 rather than px-3: five uppercase role headings plus the action column
  // otherwise overflow the panel and raise a horizontal scrollbar on a table
  // that would otherwise fit.
  const roleHeadCell = `${tailwindClassnames.tableCardHeadCell} whitespace-nowrap px-2 py-2 text-center`;
  // min-w keeps the action labels on one or two lines rather than stacking a
  // word per line once the role columns have taken their share.
  const actionCell =
    "min-w-[13rem] px-3 py-1.5 text-left text-[13px] font-normal text-primary-off-black transition duration-300 dark:text-primary-off-white";
  const markCell = "px-2 py-1.5 text-center";

  return (
    <span ref={containerRef} className="relative inline-flex items-center">
      {/*
        `describeChild` matters: without it MUI puts the tooltip text on the
        trigger as `aria-label`, which would replace the scoped name below with
        the same generic sentence on both of the user page's two icons. With it,
        the hint becomes `aria-describedby` and the button keeps its own name.

        `open` is controlled so the hint can be suppressed while the panel is
        up. Left uncontrolled, clicking the icon does not dismiss a tooltip the
        hover already opened, and because MUI renders it in a portal on
        document.body it is not bound by the panel's z-40 — it paints on top of
        the panel it just opened. Measured before this change: 1669px² of the
        panel covered, with the tooltip winning elementFromPoint.
      */}
      <Tooltip
        title={t("Click to see Role Permissions")}
        describeChild
        arrow
        open={hintOpen && !isOpen}
        onOpen={() => setHintOpen(true)}
        onClose={() => setHintOpen(false)}
      >
        <button
          ref={triggerRef}
          type="button"
          data-testid="role-permissions-info-trigger"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          // The scoped panel title, not a generic one: the user page mounts two
          // of these, and two buttons reading "What each role can do" are
          // indistinguishable in a screen reader's element list.
          aria-label={panelTitle}
          onClick={() => (isOpen ? close() : setIsOpen(true))}
          className="inline-flex cursor-pointer items-center rounded border-0 bg-transparent p-0 leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-storm-blue dark:focus-visible:ring-sky-blue"
        >
          <InfoIcon
            size={18}
            className="block text-off-black-light transition duration-300 dark:text-storm-blue-light"
          />
        </button>
      </Tooltip>

      {isOpen && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={panelTitle}
          tabIndex={-1}
          data-testid="role-permissions-info-panel"
          // Hidden with opacity, never `visibility`, for the one frame before
          // it has been measured: a `visibility: hidden` element cannot take
          // focus, which silently breaks the keyboard path.
          style={{
            left: position?.left ?? 0,
            top: position?.top ?? 0,
            opacity: position ? 1 : 0,
          }}
          // z-40, deliberately BELOW the app's z-50 confirmation tier: a
          // ConfirmationModal opened over one of these modals must stay on
          // top. z-40 still clears everything the panel competes with inside
          // its own stacking context — the modal panel (z-auto) and
          // react-select's non-portaled menu (z-index 1).
          //
          // `text-sm font-normal normal-case leading-relaxed tracking-normal`
          // pins every inherited text property. The user page mounts this
          // inside a `<th className={tableCardHeadCell}>`, which is 11px,
          // semibold, UPPERCASE and letter-spaced; `position: fixed` takes the
          // panel out of the table's box but not out of its inheritance, so
          // without these the whole panel — the 90-word inheritance note
          // included — rendered in block capitals there (QA Swarm on AKL-793).
          // The values are the ones `modalBody` already gives the panel in the
          // Invite and Change Role dialogs, so those two render as before.
          className={`${tailwindClassnames.tableCard} fixed z-40 flex max-h-[calc(100vh-1rem)] w-[min(48rem,calc(100vw-1rem))] flex-col gap-3 overflow-y-auto p-4 text-left text-sm font-normal normal-case leading-relaxed tracking-normal`}
        >
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-sm font-semibold text-primary-off-black transition duration-300 dark:text-primary-off-white">
              {panelTitle}
            </h3>
            <button
              type="button"
              data-testid="role-permissions-info-close"
              onClick={close}
              aria-label={t("Close")}
              className={tailwindClassnames.modalCloseButton}
            >
              &times;
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={headCell}>{t(table.actionHeadingKey)}</th>
                  {table.roles.map((role) => (
                    <th key={role.key} scope="col" className={roleHeadCell}>
                      {t(role.labelKey)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row) => (
                  <tr
                    key={row.labelKey}
                    className={tailwindClassnames.tableCardRowStatic}
                  >
                    <th scope="row" className={actionCell}>
                      {t(row.labelKey)}
                    </th>
                    {table.roles.map((role) => {
                      const granted = row.granted.includes(role.key);
                      return (
                        <td key={role.key} className={markCell}>
                          {granted ? (
                            <span
                              className="inline-block h-2 w-2 rounded-full bg-primary-green dark:bg-primary-green-light"
                              aria-hidden="true"
                            />
                          ) : (
                            <span
                              className="text-off-black-light dark:text-greyed-text"
                              aria-hidden="true"
                            >
                              &mdash;
                            </span>
                          )}
                          <span className="sr-only">
                            {granted ? t("Yes") : t("No")}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-2 rounded-lg border border-off-white-dark bg-off-white-dark/40 p-3 text-[13px] text-off-black-light transition duration-300 dark:border-storm-blue-border dark:bg-storm-blue-well dark:text-off-white-dark">
            <p>{t(inheritanceNote.leadKey)}</p>
            <p>{t(inheritanceNote.conditionKey)}</p>
            <ul className="list-disc pl-5">
              {inheritanceNote.mappings.map((mapping) => (
                <li key={mapping.fromKey}>
                  {t(inheritanceNote.mappingTemplateKey, {
                    from: t(mapping.fromKey),
                    to: t(mapping.toKey),
                  })}
                </li>
              ))}
              <li>{t(inheritanceNote.noInheritanceKey)}</li>
            </ul>
            <p>{t(inheritanceNote.persistenceKey)}</p>
          </div>
        </div>
      )}
    </span>
  );
};

export default RolePermissionsInfo;
